/**
 * The API client.
 *
 * Types come from `@afra/shared`, the same objects Fastify validates against,
 * so a route that changes shape breaks the build rather than the page.
 */
import type {
  AdminSettingKey,
  AdminOrgSummary,
  AdminUserSummary,
  AnswerResult,
  ApiError,
  ArchivedChat,
  ArchivedChatSummary,
  CreateOrgUserRequest,
  CreateStaffRequest,
  DashboardView,
  LedgerEntry,
  LedgerView,
  ChatMessage,
  JourneyContext,
  JourneyGoal,
  LegacyImportRequest,
  LegacyImportResult,
  LegacyStatus,
  Move,
  PlanRoadmapItemRequest,
  RoadmapView,
  PortalCharityPage,
  Profile,
  Role,
  SessionUser,
  Task,
  UpdateTaskRequest,
} from '@afra/shared'

export class RequestError extends Error {
  constructor(
    readonly code: string,
    readonly messageFa: string,
    readonly status: number,
    readonly retryable: boolean,
  ) {
    super(messageFa)
    this.name = 'RequestError'
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response

  /* The content type is declared only when something is actually being sent.
     Fastify rejects an empty body under `application/json` with a 400 — which
     is what silently broke every no-body POST here, sign-out included. */
  const headers: Record<string, string> = { ...((init?.headers as Record<string, string>) ?? {}) }
  /* A FormData body sets its own multipart boundary; declaring JSON would break it. */
  if (init?.body !== undefined && !(init.body instanceof FormData) && headers['Content-Type'] === undefined) {
    headers['Content-Type'] = 'application/json'
  }

  try {
    response = await fetch(path, { ...init, credentials: 'same-origin', headers })
  } catch {
    // A dropped connection is not a server error, and telling the reader to
    // check their network is more useful than "something went wrong".
    throw new RequestError('offline', 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید.', 0, true)
  }

  if (response.status === 204) return undefined as T

  const body = (await response.json().catch(() => null)) as (ApiError & Record<string, unknown>) | null

  if (!response.ok) {
    const error = body?.error
    throw new RequestError(
      error?.code ?? 'unknown',
      error?.messageFa ?? 'خطای ناشناخته‌ای رخ داد.',
      response.status,
      error?.retryable ?? false,
    )
  }

  return body as T
}

const post = <T>(path: string, body?: unknown): Promise<T> =>
  request<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) })

export type SayResult = {
  reply: {
    textFa: string
    model: string | null
    toolsUsed: string[]
    degraded: boolean
    chapterClosed: boolean
  }
  messages: ChatMessage[]
  context: JourneyContext
  showTools: boolean
}

export interface SayStreamHandlers {
  delta: (textFa: string) => void
  reset: () => void
  tool: (tool: { name: string; summaryFa: string; failed: boolean }) => void
}

/**
 * One turn over server-sent events. Resolves with the same result `say` does,
 * once the server sends `done`; the handlers see the turn while it happens.
 *
 * `EventSource` only does GET, so this reads the stream off a POST by hand.
 */
async function sayStream(
  input: { textFa: string; pickedLabelFa: string | null },
  on: SayStreamHandlers,
): Promise<SayResult> {
  let response: Response
  try {
    response = await fetch('/api/chat/say/stream', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'text/event-stream' },
      body: JSON.stringify(input),
    })
  } catch {
    throw new RequestError('offline', 'ارتباط با سرور برقرار نشد. اتصال اینترنت را بررسی کنید.', 0, true)
  }

  // Refused before the turn began (session, validation, rate limit): plain JSON.
  if (!response.ok || !response.body) {
    const body = (await response.json().catch(() => null)) as ApiError | null
    throw new RequestError(
      body?.error?.code ?? 'unknown',
      body?.error?.messageFa ?? 'خطای ناشناخته‌ای رخ داد.',
      response.status,
      body?.error?.retryable ?? false,
    )
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let result: SayResult | null = null
  let failure: string | null = null

  const dispatch = (block: string): void => {
    let event = 'message'
    let data = ''
    for (const line of block.split('\n')) {
      if (line.startsWith('event:')) event = line.slice(6).trim()
      else if (line.startsWith('data:')) data += line.slice(5).trim()
    }
    if (data === '') return
    const payload = JSON.parse(data) as Record<string, unknown>
    if (event === 'delta') on.delta(String(payload.textFa ?? ''))
    else if (event === 'reset') on.reset()
    else if (event === 'tool') on.tool(payload as { name: string; summaryFa: string; failed: boolean })
    else if (event === 'done') result = payload as unknown as SayResult
    else if (event === 'error') failure = String(payload.messageFa ?? '')
  }

  try {
    for (;;) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })
      let end = buffer.indexOf('\n\n')
      while (end !== -1) {
        dispatch(buffer.slice(0, end))
        buffer = buffer.slice(end + 2)
        end = buffer.indexOf('\n\n')
      }
    }
  } catch {
    // The server keeps going without us; the reply is stored and a reload shows it.
    throw new RequestError(
      'stream_dropped',
      'ارتباط وسط جواب قطع شد. صفحه را تازه کنید تا جواب را ببینید.',
      0,
      true,
    )
  }

  if (result) return result
  throw new RequestError(
    'stream_incomplete',
    failure || 'ارتباط وسط جواب قطع شد. صفحه را تازه کنید تا جواب را ببینید.',
    0,
    true,
  )
}

export interface LegacyCharity {
  id: number
  title: string
  city: string | null
  evaluations: number
  claimed: boolean
}

export const api = {
  volunteers: () => request<{ volunteers: VolunteerProfile[] }>('/api/volunteers'),

  createVolunteer: (input: VolunteerInput) => post<{ volunteer: VolunteerProfile }>('/api/volunteers', input),

  updateVolunteer: (id: number, input: VolunteerInput) =>
    request<{ volunteer: VolunteerProfile }>(`/api/volunteers/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  deleteVolunteer: (id: number) => request<{ ok: boolean }>(`/api/volunteers/${id}`, { method: 'DELETE' }),

  adminAdvice: () => request<{ advice: AdviceEntry[] }>('/api/admin/advice'),

  createAdvice: (input: AdviceInput) => post<{ advice: AdviceEntry }>('/api/admin/advice', input),

  updateAdvice: (id: number, input: AdviceInput) =>
    request<{ advice: AdviceEntry }>(`/api/admin/advice/${id}`, { method: 'PUT', body: JSON.stringify(input) }),

  deleteAdvice: (id: number) => request<{ ok: boolean }>(`/api/admin/advice/${id}`, { method: 'DELETE' }),
  me: () => request<{ user: SessionUser }>('/api/auth/me'),

  login: (email: string, password: string) =>
    post<{ user: SessionUser }>('/api/auth/login', { email, password }),

  logout: () => post<{ ok: boolean }>('/api/auth/logout'),

  changePassword: (currentPassword: string, newPassword: string) =>
    post<{ user: SessionUser }>('/api/auth/password', { currentPassword, newPassword }),

  journey: () => request<{ move: Move }>('/api/journey'),

  answer: (input: { turnId: number; score?: number | null; textFa?: string; notApplicable?: boolean }) =>
    post<AnswerResult>('/api/journey/answer', input),

  skip: (turnId: number, reasonFa?: string) =>
    post<{ move: Move }>('/api/journey/skip', { turnId, reasonFa }),

  ledger: () => request<LedgerView>('/api/ledger'),

  dimension: (code: string) => request<{ statements: LedgerEntry[] }>(`/api/ledger/${code}`),

  profile: () => request<Profile>('/api/profile'),

  domains: () => request<{ domains: { code: string; title_fa: string }[] }>('/api/domains'),

  setDomains: (codes: string[]) => post<unknown>('/api/profile/domains', { codes }),

  confirmFacts: (facts: { code: string; valueFa: string; valueNum?: number | null }[]) =>
    post<{ ok: boolean }>('/api/profile/facts', { facts }),

  /* ---- documents and evidence ---------------------------------------- */

  documents: () => request<{ documents: DocumentSummary[] }>('/api/documents'),

  uploadDocument: async (file: File, provenance: 'self_authored' | 'independent') => {
    const form = new FormData()
    form.append('provenance', provenance)
    form.append('file', file)
    // No Content-Type header: the browser must set the multipart boundary.
    const response = await fetch('/api/documents', {
      method: 'POST',
      credentials: 'same-origin',
      body: form,
    })
    const body = (await response.json().catch(() => null)) as
      | (ApiError & { documentId?: number; duplicate?: boolean })
      | null
    if (!response.ok) {
      throw new RequestError(
        body?.error?.code ?? 'upload_failed',
        body?.error?.messageFa ?? 'بارگذاری انجام نشد.',
        response.status,
        body?.error?.retryable ?? false,
      )
    }
    return { documentId: body?.documentId ?? 0, duplicate: body?.duplicate ?? false }
  },

  documentClaims: (id: number) =>
    request<{ document: DocumentSummary; claims: DocumentClaim[] }>(`/api/documents/${id}/claims`),

  reverifyDocument: (id: number) => post<{ queued: boolean }>(`/api/documents/${id}/verify`),

  deleteDocument: (id: number) =>
    request<{ ok: boolean }>(`/api/documents/${id}`, { method: 'DELETE' }),

  /* ---- certification --------------------------------------------------- */

  readiness: () => request<Readiness>('/api/readiness'),

  answerReadiness: (input: {
    itemNo: number
    answer: 'yes' | 'no' | 'partial'
    noteFa?: string | null
    documentId?: number | null
  }) => post<Readiness>('/api/readiness', input),

  certificate: () => request<Certificate>('/api/certificate'),

  /* ---- content, reports, outcomes -------------------------------------- */

  recommendations: () =>
    request<{ recommendations: RecommendationItem[] }>('/api/recommendations'),

  markOpened: (contentId: number) => post<{ ok: boolean }>('/api/recommendations/opened', { contentId }),

  reports: () => request<{ reports: ReportSummary[] }>('/api/reports'),

  report: (id: number) => request<ReportDetail>(`/api/reports/${id}`),

  generateReport: (kind: 'progress' | 'sector') =>
    post<{ reportId: number }>('/api/reports', { kind }),

  outcomes: () => request<{ metrics: MetricSpec[]; recorded: OutcomeRow[] }>('/api/outcomes'),

  recordOutcome: (input: {
    metricCode: string
    value: number
    periodStart: string
    periodEnd: string
  }) => post<{ recorded: OutcomeRow[] }>('/api/outcomes', input),

  /* ---- fleet (Afra staff only) ----------------------------------------- */

  fleetPrimitives: () =>
    request<{ primitives: { name: string; descriptionFa: string; args: string[] }[] }>(
      '/api/fleet/primitives',
    ),

  fleetQuery: (questionFa: string) => post<FleetAnswer>('/api/fleet/query', { questionFa }),

  knowledgeGaps: () =>
    request<{ gaps: { questionFa: string; statementCode: string | null; organizations: number; occurrences: number }[] }>(
      '/api/fleet/knowledge-gaps',
    ),


  /* ---- the conversation ------------------------------------------------- */

  chat: () => request<{ messages: ChatMessage[]; showTools: boolean }>('/api/chat'),

  chatContext: () => request<JourneyContext>('/api/chat/context'),

  /**
   * Say something. The agent may look things up and write things down before
   * it answers, so this can take a few seconds — and it returns the panel as
   * well, because a tool call it made mid-reply can have changed it.
   */
  say: (input: { textFa: string; pickedLabelFa: string | null }) =>
    post<SayResult>('/api/chat/say', input),

  /** The same turn, with the reply shown as it is written. */
  sayStream,

  /** Archive the live conversation and start a fresh one. Nothing is deleted. */
  archiveChat: (carryOpenTasks: boolean) =>
    post<{ archivedId: number; newJourneyId: number; carriedTasks: number }>('/api/chat/archive', { carryOpenTasks }),

  archivedChats: () => request<{ archived: ArchivedChatSummary[] }>('/api/chat/archive'),

  archivedChat: (id: number) => request<ArchivedChat>(`/api/chat/archive/${id}`),

  setChatGoal: (input: { challengeFa?: string | null; outcomeFa?: string | null }) =>
    post<{ goal: JourneyGoal }>('/api/chat/goal', input),

  claimTask: (taskId: number, accountFa: string) =>
    post<{
      result: { verdict: 'accepted' | 'needs_more' | 'rejected'; reasoningFa: string | null; judged: boolean }
      context: JourneyContext
    }>(`/api/tasks/${taskId}/claim`, { accountFa }),

  /** The director's own verdict on one of the agent's guesses, from the panel. */
  hypothesisVerdict: (id: number, verdict: 'confirmed' | 'refuted') =>
    post<{ context: JourneyContext }>(`/api/chat/hypotheses/${id}/verdict`, { verdict }),

  /* ---- the development picture ----------------------------------------- */

  dashboard: () => request<DashboardView>('/api/dashboard'),

  roadmap: () => request<RoadmapView>('/api/roadmap'),

  planRoadmapItem: (id: number, input: PlanRoadmapItemRequest) =>
    post<RoadmapView>(`/api/roadmap/items/${id}/plan`, input),

  dismissRoadmapItem: (id: number, dismissed: boolean) =>
    post<RoadmapView>(`/api/roadmap/items/${id}/dismiss`, { dismissed }),

  /**
   * Voice input: a 16 kHz mono WAV in, the words as text out. Called once per
   * piece while recording and once for the whole recording at the end.
   * Nothing is kept on the server.
   */
  transcribe: (wav: Blob, meta: { part: 'segment' | 'final'; segments?: number; peak?: number }) => {
    const form = new FormData()
    // Fields before the file: the server reads them off the file part.
    form.append('part', meta.part)
    if (meta.segments !== undefined) form.append('segments', String(meta.segments))
    if (meta.peak !== undefined) form.append('peak', String(meta.peak))
    form.append('audio', wav, 'voice.wav')
    return request<{ textFa: string }>('/api/voice/transcribe', { method: 'POST', body: form })
  },

  /* ---- review, the organization's side -------------------------------- */

  review: () => request<{ review: ReviewSummary | null }>('/api/review'),

  submitReview: (noteFa: string | null) => post<{ review: ReviewSummary }>('/api/review/submit', { noteFa }),

  withdrawReview: () => post<{ review: ReviewSummary | null }>('/api/review/withdraw'),

  /* ---- review, the assessor's side -------------------------------------- */

  reviewQueue: () => request<{ queue: ReviewQueueRow[] }>('/api/reviews'),

  reviewDetail: (id: number) => request<ReviewDetail>(`/api/reviews/${id}`),

  startReview: (id: number) => post<ReviewDetail>(`/api/reviews/${id}/start`),

  saveReviewScore: (id: number, input: { statementCode: string; levelNo: number; finalScore: number | null; noteFa: string | null }) =>
    post<ReviewDetail>(`/api/reviews/${id}/scores`, input),

  returnReview: (id: number, noteFa: string) => post<ReviewDetail>(`/api/reviews/${id}/return`, { noteFa }),

  finalizeReview: (id: number, input: { outcome: ReviewOutcome; levelNo: number | null; noteFa: string | null }) =>
    post<ReviewDetail>(`/api/reviews/${id}/finalize`, input),

  /* ---- the plan --------------------------------------------------------- */

  tasks: () => request<{ tasks: Task[] }>('/api/tasks'),

  updateTask: (taskId: number, change: UpdateTaskRequest) =>
    post<{ tasks: Task[] }>(`/api/tasks/${taskId}`, change),

  /* ---- the platform tier (Afra staff) ---------------------------------- */

  adminOrgs: (search?: string) =>
    request<{ orgs: AdminOrgSummary[] }>(
      `/api/admin/orgs${search ? `?search=${encodeURIComponent(search)}` : ''}`,
    ),

  adminSetOrgStatus: (orgId: number, status: 'active' | 'suspended') =>
    post<{ ok: boolean }>(`/api/admin/orgs/${orgId}/status`, { status }),

  /** What the portal holds for an organization; `charityId` previews one it is not bound to yet. */
  adminLegacyStatus: (orgId: number, charityId?: number) =>
    request<LegacyStatus>(`/api/admin/orgs/${orgId}/legacy${charityId ? `?charityId=${charityId}` : ''}`),

  adminLegacyImport: (orgId: number, input: LegacyImportRequest) =>
    post<LegacyImportResult>(`/api/admin/orgs/${orgId}/legacy`, input),

  adminPortalCharities: (opts: { search?: string; page?: number } = {}) => {
    const q = new URLSearchParams()
    if (opts.search) q.set('search', opts.search)
    if (opts.page && opts.page > 1) q.set('page', String(opts.page))
    const qs = q.toString()
    return request<PortalCharityPage>(`/api/admin/portal-charities${qs ? `?${qs}` : ''}`)
  },

  adminLegacyCharities: (search: string) =>
    request<{ charities: LegacyCharity[] }>(
      `/api/admin/legacy-charities?search=${encodeURIComponent(search)}`,
    ),

  adminUsers: (opts: { orgId?: number; search?: string; staffOnly?: boolean } = {}) => {
    const q = new URLSearchParams()
    if (opts.orgId) q.set('orgId', String(opts.orgId))
    if (opts.search) q.set('search', opts.search)
    if (opts.staffOnly) q.set('staffOnly', 'true')
    const qs = q.toString()
    return request<{ users: AdminUserSummary[] }>(`/api/admin/users${qs ? `?${qs}` : ''}`)
  },

  adminCreateStaff: (input: CreateStaffRequest) =>
    post<{ user: AdminUserSummary }>('/api/admin/staff', input),

  adminCreateUser: (input: CreateOrgUserRequest) =>
    post<{ user: AdminUserSummary; orgId: number; createdOrg: boolean; backfilled: number; factsWritten: number }>(
      '/api/admin/users',
      input,
    ),

  adminSetUserStatus: (userId: number, status: 'active' | 'disabled') =>
    post<{ user: AdminUserSummary }>(`/api/admin/users/${userId}/status`, { status }),

  adminSetUserRole: (userId: number, role: Role) =>
    post<{ user: AdminUserSummary }>(`/api/admin/users/${userId}/role`, { role }),

  adminResetPassword: (userId: number, password: string, mustChangePassword = true) =>
    post<{ ok: boolean }>(`/api/admin/users/${userId}/password`, { password, mustChangePassword }),

  /** Enter a charity. Everything the app then reads is that charity's. */
  impersonate: (orgId: number, reason?: string) =>
    post<{ user: SessionUser }>('/api/admin/impersonate', { orgId, reason: reason ?? null }),

  endImpersonation: () => post<{ user: SessionUser }>('/api/admin/impersonate/end'),

  adminTrail: () => request<{ entries: TrailEntry[] }>('/api/admin/trail'),

  adminSentiment: (days: number, audience: SentimentAudience = 'charity') =>
    request<SentimentReport>(`/api/admin/sentiment?days=${days}&audience=${audience}`),

  adminSettings: () => request<{ settings: Record<string, string> }>('/api/admin/settings'),

  adminSetSetting: (key: AdminSettingKey, value: string) =>
    post<{ settings: Record<string, string> }>('/api/admin/settings', { key, value }),
}

export interface VolunteerInput {
  name: string
  abilities: string
}

export interface VolunteerProfile extends VolunteerInput {
  id: number
  createdOn: string
  updatedOn: string
}

export interface AdviceInput {
  situation: string
  advice: string
  active: boolean
}

export interface AdviceEntry extends AdviceInput {
  id: number
  createdOn: string
  updatedOn: string
}

export interface SentimentCounts {
  total: number
  rated: number
  positive: number
  neutral: number
  negative: number
  upset: number
}

export type SentimentAudience = 'charity' | 'staff'

export interface SentimentReport {
  sinceDays: number
  audience: SentimentAudience
  /** Messages from the other audience in the same period, which this report leaves out. */
  otherAudienceMessages: number
  overall: SentimentCounts
  orgs: (SentimentCounts & { orgId: number; orgName: string })[]
  upset: {
    messageId: number
    orgId: number
    orgName: string
    textFa: string
    previousAgentFa: string | null
    tone: string
    probability: number
    decidedBy: string
    at: string
  }[]
}

export interface TrailEntry {
  id: number
  orgId: number | null
  orgName: string | null
  userEmail: string | null
  actingEmail: string | null
  action: string
  entity: string | null
  entityId: string | null
  createdOn: string
}


/* -------------------------------------------------------------------------- */
/* response shapes not carried by @afra/shared                                 */
/* -------------------------------------------------------------------------- */

export interface DocumentSummary {
  id: number
  filename: string
  byteSize?: number
  detectedType: string | null
  provenance?: string
  status: string
  statusDetail: string | null
  pageCount: number | null
  uploadedOn?: string
}

export interface DocumentClaim {
  statementCode: string
  levelNo: number
  statementFa: string
  verdict: 'supports' | 'contradicts' | 'silent'
  quoteFa: string | null
  pageNo: number | null
  score: number | null
  confidence: number
  model: string | null
}

export interface ReadinessItem {
  no: number
  kind: string
  required: boolean
  textFa: string
  accept: string | null
  answer: 'yes' | 'no' | 'partial' | null
  noteFa: string | null
  documentId: number | null
}

export interface Readiness {
  items: ReadinessItem[]
  answered: number
  total: number
  requiredMet: number
  requiredTotal: number
  ready: boolean
}

export interface KeyStatementStanding {
  code: string
  textFa: string
  score: number | null
  basis: string | null
  meets: boolean
}

export interface LevelStanding {
  levelNo: number
  tier: string
  tierEn: string | null
  levelTitleFa: string
  percent: number
  known: number
  total: number
  keyStatements: KeyStatementStanding[]
  dimensions: { code: string; titleFa: string; percent: number; statements: number; meets: boolean }[]
  verdict: 'awarded' | 'conditional' | 'not_yet'
  conditionalReasonFa: string | null
  blockingFa: string[]
  validityMonths: number | null
}

export interface Certificate {
  authoritative: boolean
  caveatsFa: string[]
  awardedLevel: number | null
  awardedTier: string | null
  conditionalLevel: number | null
  conditionalTier: string | null
  levels: LevelStanding[]
  readiness: Readiness
  nextStepFa: string | null
}

/* ---- the assessor's review (025) ---------------------------------------- */

export type ReviewStatus = 'submitted' | 'in_review' | 'finalized' | 'returned'
export type ReviewOutcome = 'awarded' | 'conditional' | 'none'

export interface ReviewSummary {
  id: number
  status: ReviewStatus
  submittedOn: string
  submitNoteFa: string | null
  closedOn: string | null
  outcome: ReviewOutcome | null
  outcomeLevel: number | null
  outcomeNoteFa: string | null
}

export interface ReviewQueueRow {
  orgId: number
  orgName: string
  reviewId: number | null
  status: ReviewStatus | 'not_submitted'
  submittedOn: string | null
  reviewerEmail: string | null
  known: number
  total: number
  outcome: ReviewOutcome | null
}

export interface ReviewEvidence {
  documentId: number
  filename: string
  verdict: 'supports' | 'contradicts'
  quoteFa: string | null
  pageNo: number | null
  score: number | null
}

export interface ReviewStatement {
  code: string
  levelNo: number
  dimensionCode: string
  dimensionTitleFa: string
  textFa: string
  scoringFa: string | null
  isKey: boolean
  selfScore: number | null
  selfBasis: string | null
  rationaleFa: string | null
  evidence: ReviewEvidence[]
  finalScore: number | null
  noteFa: string | null
}

export interface ReviewDetail {
  review: ReviewSummary & { orgId: number; orgName: string; reviewerEmail: string | null; mine: boolean }
  statements: ReviewStatement[]
  current: Certificate
  withReview: Certificate
}

export interface RecommendationItem {
  contentId: number
  kind: string
  titleFa: string
  url: string | null
  summaryFa: string | null
  statementCode: string
}

export interface ReportSummary {
  id: number
  kind: string
  titleFa: string
  status: string
  generatedOn: string
}

export interface ReportDetail extends ReportSummary {
  model: string | null
  sections: { headingFa: string; bodyFa: string; thin: boolean; calls: unknown }[]
}

export interface MetricSpec {
  code: string
  titleFa: string
  unitFa: string
  standard: string | null
  standardCode: string | null
  caveatFa: string | null
  neverScore: boolean
}

export interface OutcomeRow {
  metricCode: string
  titleFa: string
  value: number
  unit: string | null
  basis: string
  periodStart: string
  periodEnd: string
  standard: string | null
  standardCode: string | null
  caveatFa: string | null
  neverScore: boolean
}

export interface FleetAnswer {
  questionFa: string
  narrationFa: string
  calls: {
    primitive: string
    args: Record<string, unknown>
    sampleSize: number
    rows: Record<string, unknown>[]
    suppressedFa?: string
  }[]
  model: string | null
  degraded: boolean
}
